import { useAuth } from '@/contexts/AuthContext'
import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Shield, ArrowRight, Users, TrendingUp, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ProductFeatures, DemoAccess, LiveMetrics, SocialProof } from '@/components/homepage'

const Index = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (user?.email_confirmed_at) {
      navigate('/dashboard')
    }
  }, [user, navigate])
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Logo */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-8">
              <Shield className="w-10 h-10 text-primary" />
            </div>
            
            {/* Headline */}
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Stop Fraudulent Form Entries Before They Cost You
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Real-time fraud detection that saves businesses thousands monthly with 95%+ accuracy and sub-100ms response times.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/auth/request-access">
                  Request Account
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/auth/login">
                  Sign In
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
        >
          <Card>
            <CardHeader>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <CardTitle>Real-time Detection</CardTitle>
              <CardDescription>
                Detect fraudulent patterns instantly with 95%+ accuracy and sub-100ms response times
              </CardDescription>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <CardTitle>Financial Impact</CardTitle>
              <CardDescription>
                Track ROI with detailed savings reports and cost-per-lead analytics
              </CardDescription>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <CardTitle>Team Management</CardTitle>
              <CardDescription>
                Collaborate with role-based access and shared fraud detection insights
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <CardTitle>Bulk Processing</CardTitle>
              <CardDescription>
                Process thousands of leads simultaneously with CSV import and export
              </CardDescription>
            </CardHeader>
          </Card>
        </motion.div>

        {/* Demo Access */}
        <DemoAccess />
        
        {/* Product Features */}
        <ProductFeatures />
        
        {/* Live Metrics */}
        <LiveMetrics />
      </div>
      
      {/* Social Proof */}
      <SocialProof />
    </div>
  );
};

export default Index;
